import { Print, Visibility } from "@mui/icons-material";
import {
  Box,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { OrderModel } from "../../../data/models/order/OrderModel";
import {
  PAYMENT_METHOD_LABELS,
  TypePaymentMethodEnum,
} from "../../../data/models/order/PaymentModel";

interface CashOrdersTableProps {
  orders: OrderModel[];
  printCopy: (orderData: OrderModel) => Promise<void>;
}

export default function CashOrdersTable({
  orders,
  printCopy,
}: CashOrdersTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedOrder, setSelectedOrder] = useState<OrderModel | null>(null);

  const columns: TableColumn<OrderModel>[] = [
    {
      name: "#",
      selector: (row: OrderModel) => row.fiscalNumber ?? row.id.slice(0, 6),
      sortable: true,
    },
    {
      name: "Fecha",
      selector: (row: OrderModel) => row.createdAt.toLocaleString(),
      sortable: true,
    },
    {
      name: "Módulo",
      selector: (row: OrderModel) => row.moduleName ?? "—",
      sortable: true,
    },
    {
      name: "Pago",
      cell: (row: OrderModel) => (
        <Chip
          label={
            PAYMENT_METHOD_LABELS[row.paymentMethod! as TypePaymentMethodEnum]
          }
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      name: "Total",
      selector: (row: OrderModel) => `$${row.total.toLocaleString()}`,
      right: true,
      sortable: true,
    },
    {
      name: "Estado",
      cell: (row: OrderModel) => (
        <Chip
          label={row.status}
          size="small"
          color={row.status === "PAID" ? "success" : "default"}
        />
      ),
    },
    {
      name: "Acción",
      cell: (row: OrderModel) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => setSelectedOrder(row)}
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={() => printCopy(row)}
          >
            <Print fontSize="small" />
          </IconButton>
        </Stack>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const sortedOrders = useMemo(() => {
    return [...orders].sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    );
  }, [orders]);

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          p: 2,
          my: 2,
          overflowX: "auto",
        }}
      >
        <Typography fontWeight={600} mb={1}>
          Órdenes de la Caja
        </Typography>

        <DataTable
          fixedHeader
          fixedHeaderScrollHeight="400px"
          columns={columns}
          data={sortedOrders}
          pagination
          highlightOnHover
          striped
          noDataComponent="No hay órdenes registradas"
          paginationComponentOptions={{
            rowsPerPageText: "Filas por página",
            rangeSeparatorText: "de",
          }}
          customStyles={{
            table: {
              style: {
                width: isMobile ? "80vw" : "100%",
                backgroundColor: theme.palette.background.default,
              },
            },
            headRow: {
              style: {
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            },
            rows: {
              style: {
                minHeight: "48px",
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            },
            pagination: {
              style: {
                borderTop: `1px solid ${theme.palette.divider}`,
              },
            },
          }}
        />
      </Card>

      <OrderDetailDialog
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />
    </>
  );
}

interface OrderDetailDialogProps {
  order: OrderModel | null;
  onClose: () => void;
}

function OrderDetailDialog({ order, onClose }: OrderDetailDialogProps) {
  if (!order) return null;

  return (
    <Dialog open={!!order} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Orden #{order.fiscalNumber ?? order.id}</DialogTitle>

      <DialogContent>
        <Stack gap={2}>
          {/* Info general */}
          <Box>
            <Typography variant="body2" color="text.secondary">
              Fecha
            </Typography>
            <Typography>{order.createdAt.toLocaleString()}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Método de pago
            </Typography>
            <Typography>{order.paymentMethod ?? "—"}</Typography>
          </Box>

          <Divider />

          {/* Productos */}
          <Typography fontWeight={600}>Productos</Typography>

          <Stack gap={1}>
            {order.items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom: "1px dashed",
                  borderColor: "divider",
                  pb: 0.5,
                }}
              >
                <Box>
                  <Typography fontSize={14}>
                    {item.quantity} × {item.product.name}
                  </Typography>
                  {item.comments && (
                    <Typography fontSize={12} color="text.secondary">
                      {item.comments}
                    </Typography>
                  )}
                </Box>
                <Typography fontWeight={600}>
                  ${item.totalPrice.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Divider />

          {/* Totales */}
          <Stack gap={0.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Subtotal</Typography>
              <Typography>${order.subtotal.toLocaleString()}</Typography>
            </Stack>

            {order.discountValue > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography>Descuento</Typography>
                <Typography color="error">
                  -${order.discountValue.toLocaleString()}
                </Typography>
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight={700}>Total</Typography>
              <Typography fontWeight={700}>
                ${order.total.toLocaleString()}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
